import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Globe, Eye, Edit, ExternalLink } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, Badge } from '@mawkaa/ui';
import { useSitesStore } from '@/stores/sitesStore';
import { useAuthStore } from '@/stores/authStore';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { sites, isLoading, fetchSites } = useSitesStore();

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const recentSites = sites.slice(0, 3);
  const publishedCount = sites.filter((s) => s.status === 'published').length;

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div>
        <h1 className="text-2xl font-bold">
          Bonjour, {user?.firstName || 'Bienvenue'} !
        </h1>
        <p className="text-muted-foreground mt-1">
          Voici un aperçu de vos sites web
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total des sites
            </CardTitle>
            <Globe className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sites.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sites publiés
            </CardTitle>
            <Eye className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{publishedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Plan actuel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{user?.plan || 'Free'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent sites */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sites récents</h2>
          <Link to="/dashboard/sites" className="text-sm text-primary hover:underline">
            Voir tous
          </Link>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-2/3 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : recentSites.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-3">
            {recentSites.map((site) => (
              <Card key={site.id} className="group hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{site.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {site.subdomain}.mawkaa.tn
                      </p>
                    </div>
                    <Badge variant={site.status === 'published' ? 'success' : 'secondary'}>
                      {site.status === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/builder/${site.id}`)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Modifier
                    </Button>
                    {site.status === 'published' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`https://${site.subdomain}.mawkaa.tn`, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Voir
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Globe className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">Aucun site pour le moment</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Créez votre premier site web en quelques minutes
              </p>
              <Button onClick={() => navigate('/templates')}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un site
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
